/**
 * The registry for AnimationPlayer value tracks that are not transforms
 * (ADR-0016, ADR-0017). The mixer drives transforms only (ADR-0011), so the
 * player pushes each sampled value to a setter the target registered. The
 * stable context re-renders only the target whose value changes.
 */
import { useEffect, useRef, useState } from 'react';
import { useNodePath } from './NodePathContext';
import { createPushStoreContext } from './pushStore';
/** The key of a node's property: property names never contain `:`, so this is unambiguous. */
export function animatedValueKey(nodePath, property) {
    return `${nodePath}:${property}`;
}
const { Provider: AnimatedValueProvider, useStore: useAnimatedValueRegistry } = createPushStoreContext('AnimatedValueContext');
export { AnimatedValueProvider, useAnimatedValueRegistry };
/**
 * The decoded pushed value of this node's `property`, or `null` when none is
 * pushed and the authored value shows. `decode` is read through a ref, so an
 * inline lambda does not re-subscribe every render.
 */
export function useAnimatedValue(property, decode) {
    return useAnimatedValueAt(useNodePath(), property, decode);
}
/**
 * {@link useAnimatedValue} for the node at `nodePath`, for a caller that renders the node's own
 * provider, where `useNodePath` still answers with the parent's path.
 */
export function useAnimatedValueAt(nodePath, property, decode) {
    const registry = useAnimatedValueRegistry();
    const [value, setValue] = useState(null);
    const decodeRef = useRef(decode);
    decodeRef.current = decode;
    useEffect(() => {
        if (nodePath === null)
            return;
        return registry.subscribe(animatedValueKey(nodePath, property), (v) => setValue(v === null ? null : decodeRef.current(v)));
    }, [nodePath, registry, property]);
    return value;
}
//# sourceMappingURL=AnimatedValueContext.js.map